import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Pencil, Check, X } from "lucide-react";
import { clsx } from "clsx";
import type { ProceedingType } from "../types";
import { apiErr } from "../utils";

const OTHER = "__other__";

interface Props {
  types: ProceedingType[];
  value: string;
  onChange: (value: string) => void;
  /** Show the per-row edit button (director-only; the rename is also gated on the backend). */
  canEdit: boolean;
  /** Resolve on success, reject with the API error on failure. */
  onRename: (id: string, name: string) => Promise<void>;
}

const rowCls = (selected: boolean) =>
  clsx(
    "text-left px-3 py-2 text-sm flex items-center hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-900 dark:text-gray-100",
    selected && "bg-[var(--color-primary)]/10 font-medium text-[var(--color-primary)]",
  );

/**
 * "Typ sprawy" picker. A native <select> can't host per-option buttons, so this is a custom
 * dropdown: each type row is two columns — the name (wider, selects the type) and an edit
 * button (narrower, director-only) that swaps the name for an inline rename field.
 */
export function ProceedingTypeSelect({ types, value, onChange, canEdit, onRename }: Props) {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Portal to <body> so the dropdown isn't clipped by the card/scroll overflow.
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);

  const updateCoords = () => {
    const r = containerRef.current?.getBoundingClientRect();
    if (r) setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
  };

  const label =
    value === ""
      ? "— wybierz —"
      : value === OTHER
        ? "Inny (dodaj nowy)"
        : (types.find((t) => t.id === value)?.name ?? "— wybierz —");

  const cancelEdit = () => {
    setEditId(null);
    setEditName("");
    setErr("");
  };

  const select = (v: string) => {
    onChange(v);
    setOpen(false);
    cancelEdit();
    triggerRef.current?.focus();
  };

  const startEdit = (t: ProceedingType) => {
    setEditId(t.id);
    setEditName(t.name);
    setErr("");
  };

  const saveEdit = async () => {
    const name = editName.trim();
    if (!name || !editId || saving) return;
    setSaving(true);
    setErr("");
    try {
      await onRename(editId, name);
      cancelEdit();
    } catch (e) {
      setErr(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleOpen = () => {
    const next = !open;
    if (next) updateCoords();
    else cancelEdit();
    setOpen(next);
  };

  // Close on outside click + reposition on scroll/resize.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!containerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) {
        setOpen(false);
        cancelEdit();
      }
    };
    const reposition = () => updateCoords();
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (open) updateCoords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div ref={containerRef} className="relative mt-1">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        className={clsx("input-base w-full text-left flex items-center gap-2", value === "" && "text-gray-400 dark:text-gray-500")}
      >
        <span className="flex-1 truncate min-w-0">{label}</span>
        <ChevronDown size={14} className={clsx("shrink-0 text-gray-400 transition-transform duration-150", open && "rotate-180")} />
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ position: "fixed", top: coords.top, left: coords.left, width: coords.width, zIndex: 1000 }}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-xl"
          >
            <ul className="max-h-60 overflow-y-auto py-1">
              <li>
                <button type="button" onClick={() => select("")} className={clsx(rowCls(value === ""), "w-full text-gray-400")}>
                  <span className="flex-1 truncate">— wybierz —</span>
                </button>
              </li>

              {types.map((t) => (
                <li key={t.id} className="flex items-stretch">
                  {editId === t.id ? (
                    <div className="flex items-center gap-1 w-full px-2 py-1.5">
                      <input
                        autoFocus
                        className="input-base text-xs py-1 flex-1 min-w-0"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            saveEdit();
                          }
                          if (e.key === "Escape") {
                            e.preventDefault();
                            cancelEdit();
                          }
                        }}
                      />
                      <button
                        type="button"
                        title="Zapisz"
                        disabled={!editName.trim() || saving}
                        onClick={saveEdit}
                        className="p-1 rounded hover:bg-green-100 text-green-600 disabled:opacity-40 shrink-0"
                      >
                        <Check size={14} />
                      </button>
                      <button type="button" title="Anuluj" onClick={cancelEdit} className="p-1 rounded hover:bg-red-100 text-red-400 shrink-0">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* wider column — the type name (selects it) */}
                      <button type="button" onClick={() => select(t.id)} className={clsx(rowCls(value === t.id), "flex-1 min-w-0")}>
                        <span className="truncate">{t.name}</span>
                      </button>
                      {/* narrower column — edit (director-only) */}
                      {canEdit && (
                        <button
                          type="button"
                          title="Zmień nazwę"
                          onClick={() => startEdit(t)}
                          className="shrink-0 px-3 flex items-center justify-center border-l border-gray-100 dark:border-gray-700/50 text-[var(--color-text-muted)] hover:bg-amber-100 hover:text-amber-600"
                        >
                          <Pencil size={12} />
                        </button>
                      )}
                    </>
                  )}
                </li>
              ))}

              <li>
                <button type="button" onClick={() => select(OTHER)} className={clsx(rowCls(value === OTHER), "w-full")}>
                  <span className="flex-1 truncate">Inny (dodaj nowy)</span>
                </button>
              </li>
            </ul>
            {err && (
              <p className="px-3 py-1.5 text-[10px] font-mono text-red-500 border-t border-gray-100 dark:border-gray-700/50">{err}</p>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
